import React, { useEffect, useRef, useState } from 'react';
import MusicLibraryPicker, { MusicTrack } from './MusicLibraryPicker';
import { buildMusicProxyUrl, fetchWithAuth } from '../utils/vpsApi';

export interface MusicSelection {
  track: MusicTrack;
  startSec: number; // absolute within full track
  clipSec: number;  // length of the clip to loop
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (sel: MusicSelection) => void;
  initial?: MusicSelection | null;
  defaultClipSec?: number; // default length like Instagram (e.g., 15s)
  maxClipSec?: number;     // max selectable length (e.g., 120s)
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const proxiedAudioURL = (url: string) => buildMusicProxyUrl(url);

const MusicTrimModal: React.FC<Props> = ({ open, onClose, onConfirm, initial = null, defaultClipSec = 15, maxClipSec = 120 }) => {
  const [step, setStep] = useState<'pick' | 'trim'>('pick');
  const [track, setTrack] = useState<MusicTrack | null>(initial?.track || null);
  const [startSec, setStartSec] = useState<number>(initial?.startSec || 0);
  const [clipSec, setClipSec] = useState<number>(initial?.clipSec || defaultClipSec);
  const [duration, setDuration] = useState<number>(0);
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);
  const pauseTimeoutRef = useRef<number | null>(null);
  const [wave, setWave] = useState<number[]>([]);
  const [showPreparing, setShowPreparing] = useState<boolean>(false);
  const prepTimerRef = useRef<number | null>(null);
  const [loadError, setLoadError] = useState<boolean>(false);

  // Web Audio preview engine
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const srcRef = useRef<AudioBufferSourceNode | null>(null);
  const decodedBufferRef = useRef<AudioBuffer | null>(null);
  const isWebAudioReadyRef = useRef<boolean>(false);
  const [webReady, setWebReady] = useState<boolean>(false);
  const bootstrappedRef = useRef<boolean>(false);

  const ensureAudioContext = async () => {
    if (!audioCtxRef.current) {
      const AC = (window.AudioContext || (window as any).webkitAudioContext);
      audioCtxRef.current = new AC();
      gainRef.current = audioCtxRef.current.createGain();
      gainRef.current.connect(audioCtxRef.current.destination);
      (gainRef.current as GainNode).gain.value = 0;
    }
    if (audioCtxRef.current.state === 'suspended') {
      try { await audioCtxRef.current.resume(); } catch {}
    }
  };

  const stopPreview = (immediate = false) => {
    const g = gainRef.current; const ctx = audioCtxRef.current; const src = srcRef.current;
    if (g && ctx) {
      const now = ctx.currentTime;
      try { g.gain.cancelScheduledValues(now); } catch {}
      try { g.gain.setTargetAtTime(0, now, immediate ? 0.001 : 0.02); } catch {}
    }
    if (src) { try { src.stop(); } catch {} src.disconnect(); srcRef.current = null; }
  };

  const startPreview = async (s: number, len: number) => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;
    const g = gainRef.current;
    if (!ctx || !g) return;
    // If we don't have a decoded buffer yet, we're still preparing — just return.
    if (!decodedBufferRef.current) { return; }
    // Stop any current source with a tiny fade
    stopPreview(false);
    const src = ctx.createBufferSource();
    src.buffer = decodedBufferRef.current;
    src.loop = true;
    src.loopStart = Math.max(0, s);
    src.loopEnd = Math.max(0.01, s + Math.max(0.01, len));
    src.connect(g);
    try { src.start(0, Math.max(0, s)); } catch {}
    srcRef.current = src;
    const now = ctx.currentTime;
    try {
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value || 0, now);
      g.gain.setTargetAtTime(1, now, 0.02);
    } catch {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    setStep(track ? 'trim' : 'pick');
  }, [open, track]);

  // Decode audio buffer for smooth preview and set duration
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!open || !track) { decodedBufferRef.current = null; setWebReady(false); return; }
        await ensureAudioContext();
  const resp = await fetchWithAuth(proxiedAudioURL(track.audioURL), { mode: 'cors', cache: 'force-cache' }).catch(() => null);
        if (!resp || !resp.ok) throw new Error('music-load-failed');
  const arr = await resp.arrayBuffer();
  const ctx = audioCtxRef.current!;
  // Decode without copying the buffer to reduce overhead
  const buf = await ctx.decodeAudioData(arr);
        if (!cancelled) {
          decodedBufferRef.current = buf; isWebAudioReadyRef.current = true; setWebReady(true);
          const dur = isFinite(buf.duration) && buf.duration > 0 ? buf.duration : 0;
          setDuration(dur);
          if (dur > 0) {
            setStartSec((s) => clamp(s, 0, Math.max(0, dur - 0.5)));
            setClipSec((c) => clamp(c, 1, Math.min(maxClipSec, Math.max(1, dur))));
          }
        }
      } catch {
        decodedBufferRef.current = null; isWebAudioReadyRef.current = false; setWebReady(false);
        if (!cancelled) {
          if (prepTimerRef.current) { clearTimeout(prepTimerRef.current); prepTimerRef.current = null; }
          setShowPreparing(false);
          setLoadError(true);
        }
      }
    };
    setLoadError(false);
    run();
    return () => { cancelled = true; };
  }, [open, track, maxClipSec]);

  // (removed duplicate decode effect)

  // When WebAudio becomes ready, switch from HTML fallback to seamless loop
  useEffect(() => {
    if (!open || !track) return;
    if (webReady) {
      if (prepTimerRef.current) { clearTimeout(prepTimerRef.current); prepTimerRef.current = null; }
      setShowPreparing(false);
    }
    if (!webReady) {
      if (!prepTimerRef.current) {
        prepTimerRef.current = window.setTimeout(() => setShowPreparing(true), 150);
      }
      return;
    }
    startPreview(startSec, clipSec);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, track, webReady, startSec, clipSec]);

  // Ensure autoplay starts after a guaranteed user gesture (in case browser blocks autoplay)
  useEffect(() => {
    if (!open) return;
    const onFirst = async () => {
      try { await ensureAudioContext(); } catch {}
      if (!bootstrappedRef.current) {
        bootstrappedRef.current = true;
        startPreview(startSec, clipSec);
      }
      document.removeEventListener('pointerdown', onFirst, true);
    };
    document.addEventListener('pointerdown', onFirst, true);
    return () => { document.removeEventListener('pointerdown', onFirst, true); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startSec, clipSec]);

  // Auto-play when entering trim or changing selection (WebAudio preferred)
  useEffect(() => {
    if (!open || !track) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    startPreview(startSec, clipSec);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, track, startSec, clipSec]);

  // Stop when modal closes or component unmounts
  useEffect(() => {
    if (!open) { stopPreview(true); }
    return () => { stopPreview(true); };
  }, [open]);

  // Cleanup any pending preparing timer on unmount
  useEffect(() => () => { if (prepTimerRef.current) { clearTimeout(prepTimerRef.current); prepTimerRef.current = null; } }, []);

  const percentFromSec = (sec: number) => (duration > 0 ? (sec / duration) * 100 : 0);
  const secFromClientX = (clientX: number, rect: DOMRect) => clamp(((clientX - rect.left) / rect.width) * duration, 0, duration);

  const canConfirm = !!track && duration > 0 && clipSec > 0;
  // Build a simple waveform (peaks). Prefer decoded buffer if available to avoid re-fetch
  useEffect(() => {
    let cancelled = false;
    const computeFromBuffer = (buf: AudioBuffer) => {
      const ch = buf.numberOfChannels ? buf.getChannelData(0) : null;
      if (!ch) { setWave([]); return; }
      const bins = 160;
      const block = Math.max(1, Math.floor(ch.length / bins));
      const out: number[] = new Array(bins).fill(0);
      for (let i = 0; i < bins; i++) {
        const start = i * block;
        const end = Math.min(start + block, ch.length);
        let max = 0;
        for (let j = start; j < end; j += 128) {
          const v = Math.abs(ch[j] || 0);
          if (v > max) max = v;
        }
        out[i] = Math.min(1, max);
      }
      if (!cancelled) setWave(out);
    };
    if (decodedBufferRef.current) {
      computeFromBuffer(decodedBufferRef.current);
      return () => { cancelled = true; };
    }
    setWave([]);
    return () => { cancelled = true; };
  }, [track, webReady]);

  if (!open) return null;
  return (
    // Clicking overlay closes the trim tool and returns to the editor (but not the share page)
    <div style={styles.overlay} onClick={(e) => { e.stopPropagation(); stopPreview(true); onClose(); }}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...styles.header, position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
          <strong>{step === 'pick' ? 'Add music' : 'Trim music'}</strong>
          <button onClick={() => { stopPreview(true); onClose(); }} style={styles.closeBtn}>✕</button>
        </div>

        {step === 'pick' && (
          <div style={{ padding: 12 }}>
            <MusicLibraryPicker
              open={true}
              onClose={() => { stopPreview(true); onClose(); }}
              onSelect={(t) => {
                // Set track and immediately start audible preview within this user gesture
                setTrack(t);
                setStep('trim');
                setWebReady(false);
                if (prepTimerRef.current) { clearTimeout(prepTimerRef.current); }
                prepTimerRef.current = window.setTimeout(() => setShowPreparing(true), 150);
                // Resume context and start WebAudio loop as soon as possible
                ensureAudioContext().then(() => {
                  startPreview(startSec, clipSec);
                });
              }}
            />
          </div>
        )}

        {step === 'trim' && track && (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden>♪</span>
                <div>
                  <div style={{ fontWeight: 600 }}>{track.title}</div>
                  <div style={{ fontSize: 12, color: '#5B6E6E' }}>{track.artistName}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={styles.ghostBtn} onClick={() => setStep('pick')}>Change</button>
              </div>
            </div>

            <div
              style={{ position: 'relative', height: 40, padding: '12px 0' }}
              onPointerDown={(e) => {
                const bar = (e.currentTarget as HTMLElement).querySelector('[data-bar]') as HTMLDivElement | null;
                if (!bar) return;
                const rect = bar.getBoundingClientRect();
                const sec = secFromClientX(e.clientX, rect);
                // choose nearest handle
                const distStart = Math.abs(sec - startSec);
                const distEnd = Math.abs(sec - (startSec + clipSec));
                setDragging(distStart <= distEnd ? 'start' : 'end');
                // Start or update WebAudio preview immediately while trimming
                if (pauseTimeoutRef.current) { clearTimeout(pauseTimeoutRef.current); pauseTimeoutRef.current = null; }
                if (webReady) {
                  startPreview(distStart <= distEnd ? clamp(sec, 0, duration) : clamp(startSec, 0, duration), clipSec);
                }
              }}
              onPointerMove={(e) => {
                if (!dragging) return;
                const bar = (e.currentTarget as HTMLElement).querySelector('[data-bar]') as HTMLDivElement | null;
                if (!bar) return;
                const rect = bar.getBoundingClientRect();
                const sec = secFromClientX(e.clientX, rect);
                if (dragging === 'start') {
                  const endSec = startSec + clipSec;
                  const newStart = clamp(sec, 0, Math.max(0, endSec - 1));
                  setStartSec(newStart);
                  // Audition from the new start point while dragging
                  if (webReady) { startPreview(newStart, clipSec); }
                } else if (dragging === 'end') {
                  const newClip = clamp(sec - startSec, 1, Math.min(maxClipSec, Math.max(1, duration - startSec)));
                  setClipSec(newClip);
                  // Update loop length while dragging
                  if (webReady) { startPreview(startSec, newClip); }
                }
              }}
              onPointerUp={() => {
                setDragging(null);
                // keep playing; user can pause with the button
              }}
              onPointerLeave={() => {
                setDragging(null);
              }}
            >
              <div data-bar style={{ position: 'relative', height: 36, background: '#f1f5f9', borderRadius: 6 }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', gap: 1, padding: '6px 0' }}>
                  {wave.length > 0 ? wave.map((v, i) => (
                    <div key={i} style={{ flex: 1, height: `${Math.max(6, v * 100)}%`, background: '#cbd5e1', borderRadius: 2 }} />
                  )) : (<div style={{ width: '100%', height: 2, background: '#cbd5e1' }} />)}
                </div>
                <div style={{ position: 'absolute', left: `${percentFromSec(startSec)}%`, width: `${percentFromSec(clipSec)}%`, height: '100%', background: '#0B6B6F', borderRadius: 6, opacity: 0.25 }} />
                {/* handles */}
                <div style={{ position: 'absolute', left: `calc(${percentFromSec(startSec)}% - 10px)`, top: 4, width: 20, height: 28, borderRadius: 6, background: '#0B6B6F', boxShadow: '0 0 0 2px #fff' }} aria-hidden />
                <div style={{ position: 'absolute', left: `calc(${percentFromSec(startSec + clipSec)}% - 10px)`, top: 4, width: 20, height: 28, borderRadius: 6, background: '#0B6B6F', boxShadow: '0 0 0 2px #fff' }} aria-hidden />
                {loadError && !webReady && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <span style={{ background: 'rgba(255,255,255,0.95)', color: '#c0392b', padding: '6px 10px', borderRadius: 8 }}>Couldn't load this track. Please pick another.</span>
                  </div>
                )}
                {showPreparing && !loadError && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.9)', color: '#0B6B6F', padding: '6px 10px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                      <span className="spinner" style={{ width: 12, height: 12, border: '2px solid #0B6B6F', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.9s linear infinite' }} />
                      <span>Preparing loop…</span>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ position: 'relative', zIndex: 3, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#072B2C', marginTop: 12, marginBottom: 8 }}>
                <span style={{ background: '#fff', padding: '2px 6px', borderRadius: 6 }}>Start {formatTime(startSec)}</span>
                <span style={{ background: '#fff', padding: '2px 6px', borderRadius: 6 }}>Length {Math.round(clipSec)}s</span>
              </div>
            </div>

            <div style={{ marginTop: 4 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Clip length: {Math.round(clipSec)}s</label>
              <input type="range" min={1} max={Math.min(maxClipSec, Math.max(1, Math.floor(duration || maxClipSec)))} step={1} value={clipSec} onChange={(e) => setClipSec(Number(e.target.value))} />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={styles.ghostBtn} onClick={() => { stopPreview(true); onClose(); }}>Cancel</button>
              <button
                style={styles.primaryBtn}
                onClick={() => { if (!track) return; onConfirm({ track, startSec, clipSec }); }}
                disabled={!canConfirm}
              >Use this</button>
            </div>
          </div>
        )}
        {/* WebAudio-only preview restored to avoid double playback */}
      </div>
    </div>
  );
};

export default MusicTrimModal;

const styles: { [k: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 },
  modal: { background: '#fff', borderRadius: 12, width: '95%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 30px rgba(0,0,0,0.35)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderBottom: '1px solid #eee' },
  closeBtn: { background: 'transparent', border: '1px solid #ddd', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' },
  ghostBtn: { padding: '8px 12px', background: 'transparent', color: '#0B6B6F', border: '1px solid #0B6B6F', borderRadius: 8, cursor: 'pointer' },
  primaryBtn: { padding: '8px 12px', background: '#0B6B6F', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' },
};

function formatTime(n: number): string { const mm = Math.floor(Math.max(0, n) / 60); const ss = Math.floor(Math.max(0, n) % 60); return `${mm}:${ss.toString().padStart(2, '0')}`; }
